/**
 * @see https://umijs.org/docs/max/access#access
 * 权限控制函数，支持基于远程菜单的权限验证
 * 
 * 使用说明：
 * 
 * 1. 路由权限控制：
 *    - 在 routes.ts 中使用 access: 'canAccessRoute' 来允许路由跳转
 *    - 系统不会在路由级别阻止访问，让用户能够正常跳转
 * 
 * 2. 组件内权限检查：
 *    - 使用 PermissionWrapper 组件包装需要权限控制的组件
 *    - 使用 useAccess() hook 获取权限函数
 *    - 调用 access.canAccess(path) 检查指定路径权限
 *    - 调用 access.canAccessMenu('菜单名称') 检查菜单权限
 * 
 * 3. 权限函数说明：
 *    - canAccess(path?): 检查指定路径或当前路径的访问权限
 *    - canAccessRoute(): 用于路由配置，总是返回 true
 *    - canAccessMenu(menuName): 检查指定菜单名称是否有访问权限
 *    - hasAnyMenuAccess: 检查是否有任何菜单权限
 *    - getAccessiblePaths(): 获取所有可访问的路径
 *    - getAccessibleMenus(): 获取所有可访问的菜单名称
 * 
 * 4. 组件内权限检查示例：
 *    ```tsx
 *    import PermissionWrapper from '@/components/PermissionWrapper';
 *    
 *    // 检查当前路径权限
 *    <PermissionWrapper>
 *      <MyComponent />
 *    </PermissionWrapper>
 *    
 *    // 检查指定路径权限
 *    <PermissionWrapper requiredPermission="/order/list">
 *      <OrderList />
 *    </PermissionWrapper>
 *    
 *    // 检查菜单权限
 *    <PermissionWrapper requiredMenu="订单管理">
 *      <OrderManagement />
 *    </PermissionWrapper>
 *    
 *    // 自定义无权限显示
 *    <PermissionWrapper 
 *      fallback={<div>自定义无权限提示</div>}
 *    >
 *      <MyComponent />
 *    </PermissionWrapper>
 *    ```
 * 
 * 5. 权限控制策略：
 *    - 路由级别：允许跳转，不阻止访问
 *    - 组件级别：进行实际权限检查，无权限时显示提示
 *    - 用户体验：可以正常导航，但在无权限页面显示相应提示
 */
export default function access(
  initialState: {
    currentUser?: API.CurrentUser;
    currentUserMenu?: API.MenuRoute[];
  } | undefined,
) {
  const { currentUser, currentUserMenu } = initialState ?? {};

  // 检查用户是否有指定路径的访问权限
  const hasAccess = (path: string): boolean => {
    if (!currentUser || !currentUserMenu) {
      return false;
    }

    // 递归检查菜单权限
    const checkMenuAccess = (menus: API.MenuRoute[], targetPath: string): boolean => {
      for (const menu of menus) {
        // 检查当前菜单项
        if (menu.path === targetPath) {
          return true;
        }

        // 检查子菜单
        if (menu.routes && menu.routes.length > 0) {
          if (checkMenuAccess(menu.routes, targetPath)) {
            return true;
          }
        }
      }
      return false;
    };

    return checkMenuAccess(currentUserMenu, path);
  };

  // 检查用户是否有指定菜单名称的访问权限
  const hasMenuAccess = (menuName: string): boolean => {
    if (!currentUser || !currentUserMenu) {
      return false;
    }

    const checkMenuNameAccess = (menus: API.MenuRoute[], targetName: string): boolean => {
      for (const menu of menus) {
        if (menu.name === targetName) {
          return true;
        }

        if (menu.routes && menu.routes.length > 0) {
          if (checkMenuNameAccess(menu.routes, targetName)) {
            return true;
          }
        }
      }
      return false;
    };

    return checkMenuNameAccess(currentUserMenu, menuName);
  };

  // 基于当前路径的权限检查（用于路由配置）
  const canAccess = (path?: string): boolean => {
    if (!currentUser || !currentUserMenu) {
      return false;
    }

    // 如果没有传入路径，则使用当前路径
    const targetPath = path || window.location.pathname;
    console.log('canAccess', { targetPath, currentUser, currentUserMenu });

    // 递归检查菜单权限
    const checkMenuAccess = (menus: API.MenuRoute[], targetPath: string): boolean => {
      for (const menu of menus) {
        // 检查当前菜单项
        if (menu.path === targetPath) {
          return true;
        }

        // 检查子菜单
        if (menu.routes && menu.routes.length > 0) {
          if (checkMenuAccess(menu.routes, targetPath)) {
            return true;
          }
        }
      }
      return false;
    };

    return checkMenuAccess(currentUserMenu, targetPath);
  };

  // 用于路由配置的权限检查函数（总是返回 true，让路由能够正常跳转）
  const canAccessRoute = (): boolean => {
    // 路由级别的权限检查总是返回 true，让用户能够跳转到其他页面
    // 实际的权限检查在组件内部进行
    return true;
  };

  return {
    // 原有的管理员权限检查
    canAdmin: currentUser && currentUser.role === 'ADMIN',

    // 基于路径的权限检查
    canAccess,

    // 用于路由配置的权限检查函数（总是返回 true，让路由能够正常跳转）
    canAccessRoute,

    // 基于菜单名称的权限检查
    canAccessMenu: hasMenuAccess,

    // 检查是否有任何菜单权限
    hasAnyMenuAccess: currentUser && currentUserMenu && currentUserMenu.length > 0,

    // 获取用户可访问的所有路径
    getAccessiblePaths: () => {
      if (!currentUserMenu) return [];

      const paths: string[] = [];
      const collectPaths = (menus: API.MenuRoute[]) => {
        for (const menu of menus) {
          if (menu.path) {
            paths.push(menu.path);
          }
          if (menu.routes && menu.routes.length > 0) {
            collectPaths(menu.routes);
          }
        }
      };

      collectPaths(currentUserMenu);
      return paths;
    },

    // 获取用户可访问的所有菜单名称
    getAccessibleMenus: () => {
      if (!currentUserMenu) return [];

      const menuNames: string[] = [];
      const collectMenuNames = (menus: API.MenuRoute[]) => {
        for (const menu of menus) {
          if (menu.name) {
            menuNames.push(menu.name);
          }
          if (menu.routes && menu.routes.length > 0) {
            collectMenuNames(menu.routes);
          }
        }
      };

      collectMenuNames(currentUserMenu);
      return menuNames;
    }
  };
}
